// src/components/institution/manager/AboutSectionTab.jsx
import React, { useState, useEffect } from 'react';
import { 
  Save, 
  FileText, 
  Target, 
  Award, 
  Heart, 
  Plus, 
  Trash2, 
  Edit,
  Eye,
  Building
} from 'lucide-react';
import { institutionService } from '../../../services/institutionService';

export const AboutSectionTab = ({ bookId, institutionData, onUpdate }) => {
  const [aboutData, setAboutData] = useState({
    aboutContent: '',
    mission: '',
    vision: '',
    values: [],
    establishedYear: '',
    accreditation: '',
    achievements: [],
    leadership: '',
    philosophy: ''
  });

  const [saving, setSaving] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (institutionData) {
      const settings = institutionData.institutionSettings || {};
      setAboutData({
        aboutContent: settings.aboutContent || institutionData.aboutContent || '',
        mission: settings.mission || '',
        vision: settings.vision || '',
        values: settings.values || [],
        establishedYear: settings.establishedYear || '',
        accreditation: settings.accreditation || '',
        achievements: settings.achievements || [],
        leadership: settings.leadership || '',
        philosophy: settings.philosophy || ''
      });
    }
  }, [institutionData]);

  const handleInputChange = (field, value) => {
    setAboutData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayAdd = (field) => {
    setAboutData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleArrayUpdate = (field, index, value) => {
    setAboutData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleArrayRemove = (field, index) => {
    setAboutData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedData = {
        aboutContent: aboutData.aboutContent,
        institutionSettings: {
          ...institutionData?.institutionSettings,
          ...aboutData
        }
      };

      await institutionService.updateInstitution(bookId, updatedData);
      await onUpdate();
      alert('About section updated successfully!');
    } catch (error) {
      console.error('Error updating about section:', error);
      alert('Failed to update about section');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">About Section Configuration</h2>
          <p className="text-gray-600 mt-2">Configure your institution's about section, mission, and values</p>
        </div>
        
        <div className="flex gap-3">
          <button
            onClick={() => setPreviewMode(!previewMode)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Eye className="h-4 w-4" />
            {previewMode ? 'Edit' : 'Preview'}
          </button>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {previewMode ? (
        /* Preview Mode */
        <div className="bg-gray-50 rounded-xl p-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-indigo-600 mb-6">
                About {institutionData?.botName || 'Our Institution'}
              </h2>
              <div className="w-24 h-1 bg-indigo-600 mx-auto rounded mb-8"></div>
              
              {aboutData.aboutContent && (
                <div className="text-lg text-gray-700 leading-relaxed">
                  {aboutData.aboutContent}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
              {aboutData.mission && (
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6 mx-auto">
                    <Target className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-4 text-indigo-600">Our Mission</h3>
                  <p className="text-gray-600 text-center">{aboutData.mission}</p>
                </div>
              )}

              {aboutData.vision && (
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6 mx-auto">
                    <Award className="h-8 w-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-4 text-indigo-600">Our Vision</h3>
                  <p className="text-gray-600 text-center">{aboutData.vision}</p>
                </div>
              )}

              {aboutData.values.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <div className="flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-6 mx-auto">
                    <Heart className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-center mb-4 text-indigo-600">Our Values</h3>
                  <ul className="text-gray-600 text-center space-y-2">
                    {aboutData.values.map((value, index) => (
                      <li key={index}>• {value}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        /* Edit Mode */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Main Content */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                About Content
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Main About Text
                </label>
                <textarea
                  value={aboutData.aboutContent}
                  onChange={(e) => handleInputChange('aboutContent', e.target.value)}
                  rows={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Describe your institution, its history, and what makes it unique..."
                />
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Target className="h-5 w-5 text-indigo-600" />
                Mission & Vision
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mission Statement
                  </label>
                  <textarea
                    value={aboutData.mission}
                    onChange={(e) => handleInputChange('mission', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="What is your institution's mission and purpose?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vision Statement
                  </label>
                  <textarea
                    value={aboutData.vision}
                    onChange={(e) => handleInputChange('vision', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="What does your institution aspire to achieve?"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Values and Additional Info */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Heart className="h-5 w-5 text-indigo-600" />
                  Core Values
                </h3>
                <button
                  onClick={() => handleArrayAdd('values')}
                  className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Add Value
                </button>
              </div>

              <div className="space-y-2">
                {aboutData.values.map((value, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleArrayUpdate('values', index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={`Value ${index + 1}`}
                    />
                    <button
                      onClick={() => handleArrayRemove('values', index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Building className="h-5 w-5 text-indigo-600" />
                Additional Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Established Year
                  </label>
                  <input
                    type="text"
                    value={aboutData.establishedYear}
                    onChange={(e) => handleInputChange('establishedYear', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., 1990"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Accreditation
                  </label>
                  <textarea
                    value={aboutData.accreditation}
                    onChange={(e) => handleInputChange('accreditation', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Accreditation details and certifications"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Educational Philosophy
                  </label>
                  <textarea
                    value={aboutData.philosophy}
                    onChange={(e) => handleInputChange('philosophy', e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Your institution's educational philosophy and approach"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Award className="h-5 w-5 text-indigo-600" />
                  Key Achievements
                </h3>
                <button
                  onClick={() => handleArrayAdd('achievements')}
                  className="text-indigo-600 hover:text-indigo-800 text-sm flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  Add Achievement
                </button>
              </div>

              <div className="space-y-2">
                {aboutData.achievements.map((achievement, index) => (
                  <div key={index} className="flex gap-2">
                    <input
                      type="text"
                      value={achievement}
                      onChange={(e) => handleArrayUpdate('achievements', index, e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      placeholder={`Achievement ${index + 1}`}
                    />
                    <button
                      onClick={() => handleArrayRemove('achievements', index)}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};